import { Base64, SHA256 } from 'jshashes';
import qsLib from 'qs';
import { error, loggedIn } from 'src/services/session/actions';
import { setSession } from 'src/services/session/store';
import store from 'src/store';

import { generateRandomString } from 'src/lib/crypto';
import { textifyError } from 'src/lib/error';
import { getEnvironment, getFeatures } from 'src/lib/featureFlags';
import { getQueryParams } from 'src/lib/history';
import { localStorage } from 'src/lib/localStorage';

const OAUTH_BEGIN_URI = `${window.location.protocol}//${window.location.host}/integration-jira-ui/oauth/begin`;
const OAUTH_REDIRECT_URI = `${window.location.protocol}//${window.location.host}/integration-jira-ui/oauth/callback`;
const PD_DOMAIN = getFeatures().loginToStaging ? 'pd-staging' : 'pagerduty';

const verifier = {
  generateVerifier() {
    // account for the overhead of going to base64
    const bytes = Math.floor(128 / 1.37);
    let randomArray = new Uint16Array(bytes);

    randomArray = crypto.getRandomValues(randomArray);
    const randomString = String.fromCharCode.apply(
      null,
      randomArray
    );

    // maximum length should be 128
    // slice could do this too
    // then we could use a larger random value
    // and shrink it
    const base64 = new Base64();
    return base64.encode(randomString)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  },

  generateChallenge(verifier) {
    const sha = new SHA256();
    return sha.b64(verifier)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
};

const postData = (url, body) => fetch(url, {
  method: 'post',
  cache: 'no-cache',
  headers: {
    Authorization: '',
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  body
}).then(response => response.json());

// The authentication flow is performed in a popup window which, due to Storage Partitioning,
// does not share the same localStorage as the main window. Instead, we use postMessage to
// communicate between the popup and the main window.
// See: https://developer.chrome.com/en/docs/privacy-sandbox/storage-partitioning/
window.addEventListener('message', (message) => {
  if (message.data.pdOAuth) {
    setSession(message.data.credentials, { clientKey: message.data.clientKey });
    store.dispatch(loggedIn(message.data.credentials));
  }
});

export const openCurityPopup = () => {
  window.open(`${OAUTH_BEGIN_URI}?clientKey=${new URLSearchParams(window.location.search).get('clientKey')}`, 'popup', 'width=600,height=600');
};

export const startCurityLogin = async () => {
  localStorage.setItem('integration_jira_oauth_state', generateRandomString(40));
  localStorage.setItem('integration_jira_oauth_current_client', new URLSearchParams(window.location.search).get('clientKey'));

  const state = localStorage.getItem('integration_jira_oauth_state');
  const codeVerifier = verifier.generateVerifier();
  const codeChallenge = verifier.generateChallenge(codeVerifier);
  const codeChallengeMethod = 'S256';

  localStorage.setItem('integration_jira_oauth_code_verifier', codeVerifier);

  const params = {
    client_id: getEnvironment().oauthClientId,
    redirect_uri: OAUTH_REDIRECT_URI,
    state,
    scope: 'openid write',
    response_type: 'code',
    code_challenge_method: codeChallengeMethod,
    code_challenge: codeChallenge,
  };

  // https://identity.pagerduty.com/oauth/authorize - domain is the same for both EU and US envs
  window.location = `https://identity.${PD_DOMAIN}.com/oauth/authorize?${qsLib.stringify(params)}`;
};

export async function finalizeCurityLogin(store) {
  const { state, code } = getQueryParams();
  if (state !== localStorage.getItem('integration_jira_oauth_state')) {
    store.dispatch(error({ error: {
      title: 'We’re having trouble connecting to your account',
      body: `Please make sure your browser is configured to allow third-party cookies.${
        localStorage.getItem('integration_jira_oauth_state') === null
          ? ' In Safari browser try disabling Preferences > Privacy > Prevent cross-site tracking'
          : ''
      }`
    } }));
    return;
  }
  const codeVerifier = localStorage.getItem('integration_jira_oauth_code_verifier');
  const { oauthClientId, curityRegion } = getEnvironment();

  const tokenUrl = `https://identity.${PD_DOMAIN}.com/oauth/token`;
  const params = {
    grant_type: 'authorization_code',
    client_id: oauthClientId,
    redirect_uri: OAUTH_REDIRECT_URI,
    code,
    state,
    code_verifier: codeVerifier
  };

  const body = qsLib.stringify(params);

  try {
    let resp = await postData(tokenUrl, body);
    const { access_token, id_token } = resp;
    if (access_token) {
      const {
        appBaseUrl,
        apiBaseUrl,
        accountBaseUrlTemplate
      } = getEnvironment();

      if (!id_token) {
        throw new Error('Auth service did not respond with id_token');
      }

      const [, base46body] = id_token.split('.');
      const decodedBody = atob(base46body);
      const idToken = JSON.parse(decodedBody);

      if (idToken.region !== curityRegion) {
        throw new Error(`Can not login to another region.
         This integration works only with ${curityRegion} accounts,
         but you provided credentials for ${idToken.region} account.`.replaceAll('\n', ''));
      }

      const clientKey = localStorage.getItem('integration_jira_oauth_current_client');
      const credentials = {
        accessToken: access_token,
        accountDomain: idToken.subdomain,
        environment: {
          appBaseUrl,
          apiBaseUrl,
          accountBaseUrl: accountBaseUrlTemplate.replace('{domain}', idToken.subdomain),
        }
      };

      // Send credentials to the main window. This is necessary because the popup window
      // does not share the same localStorage as the main window.
      window.opener.postMessage({ pdOAuth: true, clientKey, credentials }, '*');

      localStorage.setItem('integration_jira_oauth_state', undefined);
      localStorage.setItem('integration_jira_oauth_code_verifier', undefined);
      localStorage.setItem('integration_jira_oauth_current_client', undefined);
      store.dispatch(loggedIn(credentials));
    } else {
      store.dispatch(error({ error: resp?.error_description || resp?.error || 'Invalid response' }));
    }
  } catch (e) {
    store.dispatch(error({ error: textifyError(e) }));
  }
}
